import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { ApiService } from '../core/services/api.service';
import { AuthService } from '../core/services/auth.service';
import { FunctionsService } from '../core/services/functions.service';
import { shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PostsApiService {
  private tagsCache$: Observable<any> | null = null;
  constructor(
    private apiService: ApiService,
    public authService: AuthService,
    private _functions: FunctionsService
  ) { }

  getAnnouncement(): Observable<any> {
    return this.apiService.get(`/forum/get-announcement?_=${Date.now()}`);
  };

  getPosts(filter: string, page: number, tagFilter: string): Observable<any> {
    return this.apiService.get(`/forum/getposts/${filter}/${page}/${tagFilter}?_=${Date.now()}`);
  };

  getArticle(filter: string, page: number, tagFilter: string): Observable<any> {
    return this.apiService.get(`/forum/getarticles/${filter}/${page}/${tagFilter}?_=${Date.now()}`);
  };

  getNewsfeeds(): Observable<any> {
    return this.apiService.get(`/newsfeed/newsfeed?_=${Date.now()}`);
  };

  loadProductData(): Observable<any> {
    return this.apiService.get(`/products/get-main-products`);
  };

  getPostDataById(postId: string, type: string): Observable<any> {
    return this.apiService.get(`/forum/${type === 'announcement' ? 'getannouncementdata' : 'getpostdata'}/${postId}?_=${Date.now()}`).pipe(
      tap((data: any) => {
        data.type = type;
      })
    );
  }

   getTags(forceRefresh = false): Observable<any> {
    if (!this.tagsCache$ || forceRefresh) {
      this.tagsCache$ = this.apiService.get(`/forum/gettags`).pipe(
        shareReplay(1)
      );
    }
    return this.tagsCache$;
  }

  clearTagsCache() {
    this.tagsCache$ = null;
  }

  newComment(data: any, formData: FormData): Observable<any> {
    const token = this.authService.getToken();
    data.token = token;
    let responseBody: any;

    return this.apiService.post(`/forum/newcomment`, data, true).pipe(
      switchMap((res) => {
        responseBody = res;
        if (typeof data?.commentPictureKey === 'string' && (data.commentPictureKey.includes('.jpg') || data.commentPictureKey.includes('.png'))) {
          return this.apiService.upload('/users/upload-picture', formData, true).pipe(
            map(() => responseBody)
          );
        }
        return of(responseBody);
      }),
      catchError(error => {
        console.error('Create Post failed', error);
        let errorResponse = this._functions.decryptData(error.error?.data || '{}');
        return of({ error: errorResponse });
      })
    );
  }


  replyComment(data: any): Observable<any> {
    const token = this.authService.getToken();
    data.token = token
    return this.apiService.post(`/forum/newcomment`, data, true);
  };

  editComment(data: {
    anonymous: boolean; commentContent: string; commentID: string; token: string | null
  }): Observable<any> {
    const token = this.authService.getToken();
    data.token = token
    return this.apiService.post(`/forum/editcomment`, data, true);
  };

  deleteComment(data: {
    commentID: string; deleted: boolean; removed: boolean; token: string | null
  }): Observable<any> {

    const token = this.authService.getToken();
    data.token = token;
    return this.apiService.post(`/forum/deletecomment`, data, true);
  };

  castPollVote(postId: string, id: string, type: string): Observable<any> {
    const token = this.authService.getToken();
    return this.apiService.post(`/forum/${type == 'quiz' ? 'castquizvote' : 'castpollvote'}`, { id: id, postID: postId, type: type, token }, true);
  };

  upvotePost(postId: string, type: string): Observable<any> {
    const token = this.authService.getToken();
    return this.apiService.post(`/forum/upvote`, { postID: postId, type: type, token }, true);
  };

  downvotePost(postId: string, type: string): Observable<any> {
    const token = this.authService.getToken();
    return this.apiService.post(`/forum/downvote`, { postID: postId, type: type, token }, true);
  };

  createPost(apiUrl: string, data: any, formData: FormData): Observable<any> {
    const token = this.authService.getToken();
    data.token = token;
    let responseBody: any;

    return this.apiService.post(`/forum/${apiUrl}`, data, true).pipe(
      switchMap((res) => {
        responseBody = res;
        if (typeof data?.postPictureKey === 'string' && (data.postPictureKey.includes('.jpg') || data.postPictureKey.includes('.png'))) {
          return this.apiService.upload('/users/upload-picture', formData, true).pipe(
            map(() => responseBody)
          );
        }
        return of(responseBody);
      }),
      catchError(error => {
        console.error('Create Post failed', error);
        let errorResponse = this._functions.decryptData(error.error?.data || '{}');
        return of({ error: errorResponse });
      })
    );
  }


  editPost(apiUrl: string, data: any, formData: FormData): Observable<any> {
    const token = this.authService.getToken();
    data.token = token;
    let responseBody: any;

    return this.apiService.post(`/forum/${apiUrl}`, data, true).pipe(
      switchMap((res) => {
        responseBody = res;
        if (typeof data?.postPictureKey === 'string' && (data.postPictureKey.includes('.jpg') || data.postPictureKey.includes('.png'))) {
          return this.apiService.upload('/users/upload-picture', formData, true).pipe(
            map(() => responseBody)
          );
        }
        return of(responseBody);
      }),
      catchError(error => {
        console.error('Edit Post failed', error);
        let errorResponse = this._functions.decryptData(error.error?.data || '{}');
        return of({ error: errorResponse });
      })
    );
  }


  deletePost(postID: string, deleted: boolean, removed: boolean): Observable<any> {
    const token = this.authService.getToken();
    return this.apiService.post(`/forum/deletepost`, { postID, deleted, removed, token }, true);
  };

  getSearchedPost(filter: string, page: number): Observable<any> {
    return this.apiService.get(`/forum/searchposts/${filter}?postPage=${page}`);
  };

  getSearchedUser(filter: string, page: number): Observable<any> {
    return this.apiService.get(`/forum/searchusers/${filter}?userPage=${page}`);
  };

  loadMilestones(page: number): Observable<any> {
    return this.apiService.get(`/forum/getmilestones/${page}`);
  };

  loadHustles(page: number): Observable<any> {
    return this.apiService.get(`/forum/gethustles/${page}`);
  };

  loadQuizzes(page: number): Observable<any> {
    return this.apiService.get(`/forum/getquizzes/${page}`);
  };

}
